import { renderLookupLabel } from '@/components/Metadata/Fieldtypes/Latest/Select';
import { FormField } from '@/components/UI/FormFields/Latest';
import Lookup from '@/components/UI/Lookup';
import { _isNotBlank, _notNil } from '@/littledash';
import type { MetaDataType } from '@/model/Metadata.model';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError.ts';
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import '@/components/UI/FormFields/LookupOptions.scss';
import '@/components/Metadata/Fieldtypes/MetadataFields.scss';

interface SelectLookupProps {
  value?: string | Array<string>;
  isMulti?: boolean;
  name: string;
  hint?: string;
  label: string;
  required?: boolean;
  metadata: { id: string | number };
  testId?: string;
  metadataType?: MetaDataType;
}

interface SelectOption {
  label: string;
  value: string;
}

/**
 * SelectLookup field component for react-hook-form v7.
 * Renders an async paginated lookup select for metadata fields.
 */
const SelectLookup: FC<SelectLookupProps> = ({
  value,
  isMulti = false,
  name,
  hint,
  label,
  required = false,
  metadata,
  testId = 'select-lookup',
  metadataType,
}) => {
  const [apiError, setApiError] = useState<Error | null>(null);
  const { control } = useFormContext();
  const isMounted = useMountedState();

  const loadOptions = async ({ inputValue, page }: { inputValue: string; page: string | number }) => {
    if (isMounted()) {
      setApiError(null);
    }

    let url = apiRoute('metadata.options.show', { id: metadata.id });
    url = `${url}?page=${page}`;

    if (_isNotBlank(inputValue)) {
      url = `${url}&query=${encodeURIComponent(inputValue)}`;
    }

    try {
      const {
        data: { data, links },
      } = await Http.get(url);
      const { next } = links;

      return {
        options: data.map((d: { value: string }) => ({
          label: renderLookupLabel(d.value),
          value: d.value,
        })),
        hasMore: _notNil(next),
      };
    } catch (error) {
      // Ignore aborted requests (e.g. AsyncPaginate replaces an in-flight
      // load) and stale rejections that arrive after unmount — neither is a
      // real failure, and reporting them produces Sentry noise plus flaky
      // tests that share a captureException spy across cases.
      if (isMounted() && notAborted(error)) {
        setApiError(error as Error);
        ExceptionHandler.captureException(
          new InVivoError('Failed to load metadata lookup options', {
            cause: error,
            slug: 'select-lookup-latest',
          })
        );
      }
      return { options: [], hasMore: false };
    }
  };

  const customGetOptionLabel = ({ label }: { label: string }) => {
    return (
      <div className="flex items-center">
        <span className="ml1 w-100 lookup-option-label">{label}</span>
      </div>
    );
  };

  const defaultValue = (value: string | Array<string> | undefined, isMulti: boolean) => {
    if (_notNil(value)) {
      if (isMulti) {
        const valueArray = Array.isArray(value) ? value : [];
        return valueArray.map((f) => ({
          label: renderLookupLabel(f),
          value: f,
        }));
      } else {
        return {
          label: renderLookupLabel(value),
          value: value as string,
        };
      }
    }
    return undefined;
  };

  const handleChange = (
    onChange: (value: string | Array<string>) => void,
    data: SelectOption | Array<SelectOption> | null,
    isMulti: boolean
  ) => {
    if (data) {
      if (isMulti) {
        onChange((data as Array<SelectOption>).map((d) => d.value));
      } else {
        onChange((data as SelectOption).value);
      }
    } else {
      onChange('');
    }
  };

  return (
    <FormField name={name} label={label} hint={hint} required={required} testPrefix={testId}>
      <Controller
        control={control}
        defaultValue={value}
        name={name}
        rules={{
          required: required && 'This field is required',
        }}
        render={({ field: { onChange, value } }) => (
          <div className={metadataType === 'study_meta' ? 'lookup-select-container' : ''} data-testid={testId}>
            <Lookup
              isMulti={isMulti}
              defaultValue={defaultValue(value, isMulti)}
              loadOptions={loadOptions}
              handleSelect={(data: SelectOption | Array<SelectOption> | null) => handleChange(onChange, data, isMulti)}
              getOptionLabel={customGetOptionLabel}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {_notNil(apiError) && <p className="f7 red">There was a problem fetching options</p>}
          </div>
        )}
      />
    </FormField>
  );
};

export default SelectLookup;
